<template>
  <v-list-item>
    <!-- Main content (title and subtitle) -->
    <v-list-item-title>
      <v-tooltip bottom>
        <template #activator="{ props }">
          <span v-bind="props">{{ api.title }}</span>
        </template>
        <span>{{ api.name }}</span>
      </v-tooltip>
    </v-list-item-title>

    <v-list-item-subtitle>
      <v-skeleton-loader :loading="api.isLoading" type="text">
        <!-- Highlight the content if there's an error -->
        <span :class="[isError ? 'text-red font-weight-bold' : '']">
          {{ api.version }} - {{ api.releaseSignature }}
          <!-- Only show the environment in parentheses if there's no error -->
          <span v-if="!isError"> ({{ api.environment }}) </span>&nbsp;
        </span>

        <!-- Tooltip and release date, hidden if error -->
        <v-tooltip v-if="!isError" bottom>
          <template #activator="{ props }">
            <span v-bind="props"> Released: {{ ApiReleasedOn }} </span>
          </template>
          <span>{{ api.lastUpdated }}</span>
        </v-tooltip>
      </v-skeleton-loader>
    </v-list-item-subtitle>

    <!-- Right-side action button -->
    <template #append>
      <v-tooltip bottom>
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon="mdi-refresh"
            :loading="api.isLoading"
            variant="text"
            :color="isError ? 'red' : 'grey'"
            @click="refetch"
          />
        </template>
        <span>{{ api.lastFetched }}</span>
      </v-tooltip>
    </template>
  </v-list-item>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useTimeAgo } from '@vueuse/core';
  import { IApiVersion } from '@/interfaces/IApiVersion';
  import versionLogic from '@/business/versionLogic';

  const { updateApiVersionByServiceName } = versionLogic();

  // Define props to receive the full API object
  const props = defineProps<{
    api: IApiVersion;
  }>();

  // Method to handle the refresh action
  const refetch = () => {
    updateApiVersionByServiceName(props.api.name);
  };

  // Computed property for the release time tooltip
  const ApiReleasedOn = computed(() => {
    if (props.api.lastUpdated) {
      return useTimeAgo(new Date(props.api.lastUpdated)).value;
    }
    return '';
  });

  // Computed property to check if there is an error
  const isError = computed(() => props.api.version === 'Error');
</script>
